import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { selectIsAllTeamsScope, useTeamStore } from '@/stores/team-store';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';

/**
 * Group Security v3 §12 — HOME PATH (Root, path=null) action lock.
 *
 * The backend blocks a NON-admin member with `canEditHomePath=false` from creating OR editing loose
 * items at the section Root (path=null) — see open-api principal.service.__pe. This context lets
 * the FE MIRROR that: a content page (Assets/Screens/Playlists/Schedules/Engage) wraps its subtree in
 * `RootActionLockProvider` with the computed lock, and `usePermissions` reads it — so every
 * `canCreate/canEdit/canDelete` consumer under that page (toolbars, item menus, bulk bars) is disabled
 * at Root with no per-site wiring. Non-content pages never provide the context ⇒ default `false` ⇒ no
 * effect. Admins (Owner/Admin/Super Admin/Team Owner) are never locked.
 */
const RootActionLockContext = createContext(false);

const ADMIN_ROLES: string[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

export function RootActionLockProvider({
  locked,
  children,
}: {
  locked: boolean;
  children: ReactNode;
}) {
  return <RootActionLockContext.Provider value={locked}>{children}</RootActionLockContext.Provider>;
}

/** Whether the current `usePermissions` consumer sits under a Root-locked content subtree. */
export function useRootActionLock(): boolean {
  return useContext(RootActionLockContext);
}

/**
 * Compute the Root (path=null) action lock for the CURRENT member — the value a content page passes to
 * `RootActionLockProvider`. Mirrors the backend §12 gate: locked ⇔ at Root AND a non-admin AND
 * `canEditHomePath !== true`. `atRoot` = the page is showing the section Root (no folder open).
 *
 * REWRITTEN 2026-10-16 (#2486, Isabel's rule): ASK THE TEAMSPACE. We do not care how a member got
 * into a teamspace. If they are in it with a role they may edit at its Root, unless that
 * teamspace's own "Members can create or edit at the section root" toggle is off. Since the §12
 * inversion that toggle is the single writer — the per-member flag and the group grant's copy are
 * both deliberately unread — so the teamspace row carries the whole answer.
 *
 * That is why there is no per-user list here any more. The toggle is a property OF the teamspace,
 * so it rides on the teamspace rows this client already holds. A parallel list keyed by teamspace
 * id would be a second statement about the same objects, and the two could disagree.
 *
 * Both terms this replaces were dead, and neither loss widens anything:
 *
 *   · the GROUP term (`me.groupIds.length > 0`) could not change an outcome. The only teamspace
 *     where it decided alone was Default, and Default has no teamspace document to carry the
 *     toggle. It was also account-wide where the gate is per-teamspace, which is what #2486
 *     reported: the v4 migration puts EVERYONE in a group, so it read permanently true and every
 *     non-admin lost Home root in Default — the one teamspace with no toggle to restore it — for a
 *     write the API accepted.
 *   · the REAL-TEAMSPACE term is implied: only a teamspace document can carry a toggle, so only a
 *     real teamspace can ever answer `false` here. Default is unset and therefore never locked.
 *
 * It also fixes a live break the ticket did not name. This used to read `canEditHomePathTeamIds`,
 * an ALLOW-list the server stopped producing at the inversion. It arrived undefined, `?? []` made
 * it empty, and the test then locked every non-admin in every REAL teamspace — including ones whose
 * toggle is on. An allow-list fails that way by construction: missing means "allowed nowhere". A
 * flag that is only `false` when an admin says so cannot.
 *
 * Direction matters, since this file has drifted twice: stricter than the gate hides a capability
 * silently (#2486); looser produces a silent PERMISSION_DENIED (QA, 2026-08-28 — the folder dialog
 * accepted a name, sent `saveGroup`, and swallowed the refusal).
 */
export function useRootActionLockValue(atRoot: boolean): boolean {
  const me = useAuthStore((s) => s.me);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  if (!atRoot || !me) return false;
  const team = me.teams?.find((t) => t._id === activeTeamId);
  if (ADMIN_ROLES.includes(team?.role ?? '')) return false; // Owner/Admin/Super Admin/Team Owner bypass
  // The account-wide admin shortcut, ORed exactly as the gate ORs it: a root Owner or Super Admin
  // is never locked, even in a teamspace where their team role is not an admin one.
  if (me.canEditHomePath === true) return false;
  // Only an explicit `false` denies. Unset — a teamspace that never set the toggle, and Default,
  // which has no document to set it on — means allowed, which is the server's own default.
  return team?.canEditHomePath === false;
}

/** The section Root as every create payload spells it: no path, `''`, or `'/'`. */
export function isRootPath(path: string | null | undefined): boolean {
  return !path || path === '/';
}

/**
 * The create-at-path guard a DIALOG owns for itself (#2138 follow-up, #2208).
 *
 * Triggers (toolbar buttons, pickers, search quick-creates) hide or disable themselves through the
 * page provider, but the dialog or hook that finally sends the mutation used to trust whoever
 * opened it — and every one of them opens through a shared store action any new caller can reach
 * without re-implementing the trigger's gate. That is exactly how the three #2138 gaps arose. A
 * dialog that reads its OWN target path here cannot be bypassed from the frontend by construction:
 * fold `reason` into `useDialogSubmitState({ validationError })`, or refuse up front in a hook.
 *
 * Two reasons a create at the Root is refused, checked in this order:
 *   1. All Teamspaces scope (#2208, spec §1d AV-2): at the Root no folder names a target
 *      teamspace, so the destination is ambiguous — "Select a teamspace to create". Inside a
 *      folder the folder's own teamId settles it, so this contributes nothing there (same rule as
 *      `useAllScopeCreateBlock`, which the trigger layer uses).
 *   2. §12 HOME PATH lock — same predicate as `useRootActionLockValue`.
 * `reason` is the one explanation every locked surface shows, so the copy cannot drift either.
 */
export function useRootCreateLock(targetPath: string | null | undefined): {
  locked: boolean;
  reason: string | undefined;
} {
  const { t } = useTranslation();
  const atRoot = isRootPath(targetPath);
  const cohort = useGroupSecurityCohort();
  // #2452 — a legacy account can hold a STALE All scope in localStorage with no UI to clear
  // it (see useIsAllTeamScope). Without the cohort here, root create stays blocked forever
  // with 'Select a teamspace to create' and no teamspace picker to obey it.
  const allScopeBlocked = useTeamStore(selectIsAllTeamsScope) && cohort && atRoot;
  const homePathLocked = useRootActionLockValue(atRoot);
  if (allScopeBlocked) return { locked: true, reason: t('Select a teamspace to create') };
  if (homePathLocked) {
    return {
      locked: true,
      reason: t(
        'Creating at the home root is turned off for this teamspace. Open a folder to create there.',
      ),
    };
  }
  return { locked: false, reason: undefined };
}
